import { useState, useMemo } from "react";
import { Check, Copy } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";

interface GeneratePersonasModalProps {
  projectId: string;
  onClose: () => void;
}

export function GeneratePersonasModal({
  projectId: _projectId,
  onClose,
}: GeneratePersonasModalProps) {
  const [focus, setFocus] = useState("");
  const [copied, setCopied] = useState(false);

  const promptText = useMemo(() => {
    const trimmedFocus = focus.trim();
    const focusClause = trimmedFocus ? ` focusing on "${trimmedFocus}"` : "";
    return `/iamu-new Propose and create durable personas for this project${focusClause}. Ground each persona in durable working reality, device/environment context, what tools they use today, and their disposition (habits, patience level, and what makes them hesitate or abandon a flow) — never a one-off task. Then sync with Iamu.`;
  }, [focus]);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(promptText);
      setCopied(true);
      toast.success("Copied prompt for your coding agent");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Failed to copy prompt");
    }
  };

  return (
    <Dialog
      open
      onOpenChange={(isOpen) => {
        if (!isOpen) onClose();
      }}
    >
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Draft personas with your coding agent</DialogTitle>
          <DialogDescription>
            Paste this prompt into Claude Code or Cursor to propose and create
            durable personas grounded in your repository and domain context.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="persona-agent-focus">
              Focus area{" "}
              <span className="font-normal text-muted-foreground">
                (optional)
              </span>
            </Label>
            <Input
              id="persona-agent-focus"
              placeholder="e.g. First-time claimants, finance approvers, mobile commuters..."
              value={focus}
              onChange={(e) => setFocus(e.target.value)}
              autoFocus
            />
          </div>

          <div className="space-y-2">
            <Label>Agent prompt</Label>
            <pre className="whitespace-pre-wrap rounded-md border border-border bg-muted/40 p-3 font-mono text-xs leading-relaxed text-foreground">
              {promptText}
            </pre>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
          <Button onClick={handleCopy}>
            {copied ? (
              <>
                <Check className="mr-1.5 h-3.5 w-3.5" />
                Copied
              </>
            ) : (
              <>
                <Copy className="mr-1.5 h-3.5 w-3.5" />
                Copy prompt
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
